import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO, { ORG_SCHEMA } from "../components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

const renderInline = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} style={{ color: "#1A1B1A", fontWeight: 500 }}>
        {part.slice(2, -2)}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );

export default function AboutPage() {
  const { t } = useTranslation();
  const values = t("about.values", { returnObjects: true }) || [];

  return (
    <div data-testid="about-page">
      <SEO titleKey="seo.about_title" descriptionKey="seo.about_description" path="/about" schema={ORG_SCHEMA} />

      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-4"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
              {t("about.eyebrow")}
            </p>
          </motion.div>
          <motion.h1
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A1B1A] max-w-3xl"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
            {t("about.title_1")} <span style={{ fontWeight: 600 }}>{t("about.title_passion")}</span> {t("about.title_meets")} {t("about.title_3")}<span style={{ fontWeight: 600 }}>{t("about.title_excellence")}</span>
          </motion.h1>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="lg:col-span-6 img-hover-zoom">
              <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/bikcitbu_P1000600.webp"
                alt="ETERNA Motion — the art of detail" className="w-full h-[50vh] lg:h-[60vh] object-cover" data-testid="about-story-image" />
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} className="lg:col-span-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-6" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                {t("about.story_eyebrow")}
              </p>
              <h2 className="text-3xl sm:text-4xl tracking-tighter text-[#1A1B1A] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
                {t("about.story_title")}
              </h2>
              <div className="space-y-5" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">{t("about.story_p1")}</p>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">{t("about.story_p2")}</p>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">{t("about.story_p3")}</p>
              </div>
              <div className="luxury-divider mt-10" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 grain-overlay" style={{ backgroundColor: "#F3F1EC" }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
              {t("about.values_eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tighter text-[#1A1B1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
              {t("about.values_title_1")} <span style={{ fontWeight: 600 }}>{t("about.values_title_2")}</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {values.map((val, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} data-testid={`value-card-${i}`} className="relative">
                <p className="text-5xl text-[#D8D4CC] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="text-xl tracking-tight text-[#1A1B1A] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                  {val.title}
                </h3>
                <p className="text-sm text-[#4A4A4A] leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                  {val.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Limited Edition Why */}
      <section data-testid="limited-edition-why-section" className="py-24 lg:py-32" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="lg:col-span-6 order-2 lg:order-1">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-6" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                {t("about.le_eyebrow")}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#1A1B1A] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
                {t("about.le_title_1")}<br />{t("about.le_title_2")} <span style={{ fontWeight: 600 }}>{t("about.le_title_3")}</span>
              </h2>
              <div className="space-y-5" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">{renderInline(t("about.le_p1"))}</p>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">{renderInline(t("about.le_p2"))}</p>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">{renderInline(t("about.le_p3"))}</p>
                <p className="text-base text-[#1A1B1A] leading-relaxed pt-2 italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
                  {t("about.le_p4")}
                </p>
              </div>
              <Link to="/limited-edition" data-testid="about-limited-edition-cta" className="inline-block mt-10">
                <button className="btn-luxury">{t("about.le_cta")}</button>
              </Link>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} className="lg:col-span-6 order-1 lg:order-2 img-hover-zoom">
              <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/m98i44ca_P1000610.webp"
                alt="ETERNA Limited Edition" className="w-full h-[55vh] lg:h-[65vh] object-cover" data-testid="about-limited-edition-image" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section data-testid="team-section" className="py-24 lg:py-32" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-16 lg:mb-20">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
              {t("about.team_eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#1A1B1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
              {t("about.team_title_1")} <span style={{ fontWeight: 600 }}>{t("about.team_title_2")}</span>
            </h2>
          </motion.div>

          {/* CEO */}
          <div className="mb-24 lg:mb-32">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C4A462] mb-3" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                {t("about.ceo_role")}
              </p>
              <h3 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A1B1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }} data-testid="ceo-name">
                {t("about.ceo_name")}
              </h3>
              <div className="w-20 h-px bg-[#C4A462] mb-8" />
              <div className="space-y-5" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                <p className="text-base text-[#4A4A4A] leading-relaxed">{t("about.ceo_p1")}</p>
                <p className="text-base text-[#4A4A4A] leading-relaxed">{t("about.ceo_p2")}</p>
                <p className="text-base text-[#4A4A4A] leading-relaxed">{t("about.ceo_p3")}</p>
              </div>
            </motion.div>
          </div>

          {/* President */}
          <div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} className="max-w-3xl ml-auto text-right">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C4A462] mb-3" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                {t("about.president_role")}
              </p>
              <h3 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A1B1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }} data-testid="president-name">
                {t("about.president_name")}
              </h3>
              <div className="w-20 h-px bg-[#C4A462] mb-8 ml-auto" />
              <div className="space-y-5" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                <p className="text-base text-[#4A4A4A] leading-relaxed">{t("about.president_p1")}</p>
                <p className="text-base text-[#4A4A4A] leading-relaxed">{t("about.president_p2")}</p>
                <p className="text-base text-[#4A4A4A] leading-relaxed">{t("about.president_p3")}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 grain-overlay" style={{ backgroundColor: "#1A1B1A" }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="text-3xl sm:text-4xl tracking-tighter text-[#FAF9F6] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
              {t("about.cta_title")}
            </h2>
            <Link to="/contact" data-testid="about-cta-contact">
              <button className="bg-[#C4A462] text-[#FAF9F6] px-10 py-4 text-xs uppercase tracking-[0.15em] hover:bg-[#A98A4E] transition-colors duration-300"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                {t("about.cta_button")}
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
